/**
 * The times of day a therapist is actually free, as a list a picker can offer.
 *
 * `POST /appointments/empty-slots` answers with windows -- a start, an end and a length --
 * because that is what a patient chooses between on the booking page. A time control chooses
 * between *starts*, so this is the one place the first is read as the second.
 *
 * Two properties are worth knowing. Every start the API returns is already snapped to the
 * quarter hour (`snapToGrid` on the server), so the list lands on the same grid `minutesStep`
 * would have generated and nothing here rounds anything. And the windows are of the length that
 * was asked for, so a start being present already means the whole appointment fits -- there is
 * no arithmetic left to do about the end.
 */

import { EmptySlotResponse } from './models/appointment';

/**
 * How far apart the hours a diary offers are.
 *
 * The clinic books on the hour and the half hour; quarter past and quarter to are not times
 * anybody here schedules, and offering them doubled the list to say nothing new. Sent to the
 * API rather than filtered out of its answer, so what is offered and what would be accepted
 * stay one decision -- and so the count under the field describes the list beside it.
 */
export const DIARY_START_STEP_MINUTES = 30;

/**
 * The `HH:mm` starts offered on one day, in order and without repeats.
 *
 * Filtered by date rather than trusted wholesale: the endpoint takes a range and the dialogs
 * ask about a single day, but a range is what a week strip asks for and passing one of those
 * through unfiltered would put seven days of times in one dropdown.
 */
export const freeStartTimesOn = (
    slots: EmptySlotResponse[] | undefined,
    isoDay: string
): string[] => {
    const starts: string[] = (slots ?? [])
        .filter(slot => slot.startTime.slice(0, 10) === isoDay)
        .map(slot => slot.startTime.slice(11, 16));

    // Windows of different lengths can share a start, and a caller asking about a range gets
    // one list per day, so the same time can arrive twice.
    return Array.from(new Set(starts)).sort();
};
